import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AxiosController from "../../../authentication/AxiosController";
import Loader from "../../../loader/Loader";
import { notifyError, notifySuccess } from "../../../toastify/toastify";

const ShopifyIntegrations = () => {
  const { slug, lang } = useParams();
  const token = localStorage.getItem("token");
  const [_] = useTranslation("global");
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchIntegrations = () => {
    setLoading(true);
    AxiosController.get(`api/${slug}/${lang}/shopify/integrations`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        setIntegrations(res.data.integrations || []);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleDisconnect = (integrationId) => {
    setLoading(true);
    AxiosController.post(
      `api/${slug}/${lang}/shopify/integrations/${integrationId}/disconnect`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    )
      .then(() => {
        notifySuccess(_("panel.shopifyDisconnected"));
        fetchIntegrations();
      })
      .catch((e) => {
        notifyError(e?.response?.data?.message || "Error disconnecting");
        setLoading(false);
      });
  };

  const handleReconnect = (integrationId) => {
    setLoading(true);
    AxiosController.post(
      `api/${slug}/${lang}/shopify/integrations/${integrationId}/reconnect`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    )
      .then((res) => {
        if (res.data.auth_url) {
          window.location.href = res.data.auth_url;
        } else {
          fetchIntegrations();
        }
      })
      .catch((e) => {
        notifyError(e?.response?.data?.message || "Error reconnecting");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchIntegrations();
  }, [slug, lang]);

  const getStatusBadge = (status) => {
    const styles = {
      active: { backgroundColor: "rgb(1, 200, 92)", color: "white" },
      pending: { backgroundColor: "rgb(243, 168, 55)", color: "white" },
      disconnected: { backgroundColor: "rgb(165, 165, 165)", color: "white" },
      error: { backgroundColor: "rgb(220, 53, 69)", color: "white" },
    };
    const labels = {
      active: _("panel.shopifyActive"),
      pending: _("panel.shopifyPending"),
      disconnected: _("panel.shopifyDisconnect"),
      error: _("panel.shopifyFailed"),
    };
    return (
      <span className="badge" style={styles[status] || styles.pending}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div
      className="position-relative tables-container"
      style={{ minHeight: "100px", width: "100%" }}
    >
      <Loader active={loading} />
      <div
        className="tables-container d-flex flex-column px-2"
        style={{ position: "relative", flexGrow: 3 }}
      >
        <div className="footer d-flex flex-column flex-grow-1">
          {integrations.length > 0 ? (
            <div className="table-responsive" style={{ borderRadius: "40px" }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{_("panel.shopifyShopDomain")}</th>
                    <th>{_("panel.shopifyStatus")}</th>
                    <th>{_("panel.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {integrations.map((integration, index) => (
                    <tr key={integration.id}>
                      <td>{index + 1}</td>
                      <td>{integration.shop_domain}</td>
                      <td>{getStatusBadge(integration.status)}</td>
                      <td>
                        <div className="d-flex gap-2 justify-content-center">
                          {integration.status === "active" ? (
                            <button
                              className="btn btn-sm"
                              style={{
                                backgroundColor: "rgb(220, 53, 69)",
                                color: "white",
                              }}
                              onClick={() => handleDisconnect(integration.id)}
                            >
                              {_("panel.shopifyDisconnect")}
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm"
                              style={{
                                backgroundColor: "rgb(89, 198, 201)",
                                color: "white",
                              }}
                              onClick={() => handleReconnect(integration.id)}
                            >
                              {_("panel.shopifyReconnect")}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="d-flex flex-column align-items-center gap-3 py-5">
              <p className="fs-5 text-muted">
                {_("panel.shopifyNoIntegrations")}
              </p>
              <p className="text-muted">
                {_("panel.shopifyConnectFirst")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopifyIntegrations;
